import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { CreditCard } from 'lucide-react'
import { TpaLiveStatus } from '@/components/tagadapay/tpa-live-status'
import { useProcessorsList, useTagadapayAccounts, Processor } from '@/lib/api/processors'
import { tpaIdFromProcessor } from '@/lib/tagadapay-tpa-status'
import { getIntegrationImage } from '@/lib/utils/integration-utils'
import { useEffectiveBranding } from '@/hooks/use-effective-branding'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { cn } from "@/lib/utils"

interface ProcessorSelectorProps {
    processorId?: string
    onProcessorChange: (processorId: string) => void
    excludeProcessorIds?: string[]
    error?: string
    label?: string
    required?: boolean
    helpText?: string
    placeholder?: string
}

const getAvailableProcessors = (
    processors: Processor[] | undefined,
    excludeIds: string[] = [],
): Processor[] => {
    if (!processors) return []
    return processors.filter((processor) => !excludeIds.includes(processor.id) && processor.enabled)
}

/**
 * Component for selecting a single processor with filtering capability
 */
export const ProcessorSelector = ({
    processorId,
    onProcessorChange,
    excludeProcessorIds = [],
    error,
    label,
    required = false,
    helpText,
    placeholder,
}: ProcessorSelectorProps) => {
    const { t } = useTranslation(['stores', 'common'])
    const brand = useEffectiveBranding()
    const { data: processors, isLoading } = useProcessorsList()
    const { data: tpas } = useTagadapayAccounts()
    const tpaById = React.useMemo(() => new Map((tpas ?? []).map((a) => [a.id, a])), [tpas])

    const tpaChip = (processor: Processor) => {
        const tpaId = tpaIdFromProcessor(processor)
        const tpa = tpaId ? tpaById.get(tpaId) : undefined
        return (
            <TpaLiveStatus
                tpaId={tpaId}
                status={tpa?.status}
                mode={tpa?.mode}
                compact
                interactive={false}
            />
        )
    }

    const availableProcessors = React.useMemo(
        () => getAvailableProcessors(processors, excludeProcessorIds),
        [processors, excludeProcessorIds],
    )

    const selectedProcessor = React.useMemo(
        () => availableProcessors.find((p) => p.id === processorId),
        [availableProcessors, processorId],
    )

    return (
        <div className="space-y-2">
            {label && (
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    {label} {required && '*'}
                </label>
            )}
            <Select
                value={processorId ?? ''}
                onValueChange={onProcessorChange}
                disabled={isLoading || availableProcessors.length === 0}
            >
                <SelectTrigger
                    className={cn(
                        'w-full',
                        error && 'border-red-500 focus-visible:ring-red-500',
                    )}
                    aria-invalid={!!error}
                >
                    <SelectValue
                        placeholder={
                            isLoading
                                ? t('common:loading')
                                : placeholder || t('common:selectProcessor')
                        }
                    >
                        {selectedProcessor && (
                            <div className="flex min-w-0 items-center gap-2">
                                <img
                                    src={getIntegrationImage(selectedProcessor.type, brand)}
                                    alt={selectedProcessor.type}
                                    className="h-4 w-4 object-contain"
                                />
                                <span className="truncate">{selectedProcessor.name}</span>
                                {tpaChip(selectedProcessor)}
                            </div>
                        )}
                    </SelectValue>
                </SelectTrigger>
                <SelectContent>
                    {availableProcessors.length === 0 ? (
                        <div className="flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground">
                            <CreditCard className="h-4 w-4" />
                            <span>
                                {t('common:noProcessorsAvailable')}
                            </span>
                        </div>
                    ) : (
                        availableProcessors.map((processor) => (
                            <SelectItem key={processor.id} value={processor.id}>
                                <div className="flex min-w-0 items-center gap-2">
                                    <img
                                        src={getIntegrationImage(processor.type, brand)}
                                        alt={processor.type}
                                        className="h-4 w-4 object-contain"
                                    />
                                    <span className="truncate">{processor.name}</span>
                                    {tpaChip(processor)}
                                </div>
                            </SelectItem>
                        ))
                    )}
                </SelectContent>
            </Select>
            {error && (
                <p className="text-xs leading-tight text-red-600">{error}</p>
            )}
            {helpText && (
                <p className="text-muted-foreground text-xs">{helpText}</p>
            )}
        </div>
    )
}

